import * as React from "react";
import { useState, useEffect, useCallback, useMemo } from "react";
import { DataFilterTable } from "Components/Form/DataFilterTable";
import { observer } from "mobx-react";
import { SorterResult, SortOrder } from "antd/lib/table/interface";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { useTranslation } from "react-i18next";
import { I18N_NS } from "Config/i18n";
import { Dropdown, Badge, Switch, Row, Tooltip } from "antd";
import { PaginationProps } from "antd/lib/pagination";
import { API } from "General/API";
import { API_URL } from "General/APIEndpoints";
import classNames from "classnames";
import { Link } from "react-router-dom";
import { userInfoStore } from "Stores";
import { projectGroupList } from "Pages/Admin/Stores/ProjectGroupStore";
import { EditProjectGroupNameModal } from "./EditProjectGroupNameModal";
import { withModal } from "Components/General/Modal/ModalContext";
import { EditProjectDescriptionModal } from "./EditProjectDescriptionModal";
import { ProjectInformationModal } from "./ProjectInformationModal";
import { modal } from "Components/General/Modal/AntdModal";
import { ChangeFileOwner } from "./ChangeFileOwner";
import { WithModal } from "Components/General/Modal/withModal";
import { waitFor } from "utils/waitFor";
import { ProjectMembersModal } from "./ProjectMembersModal";

export interface ProjectQueryParams extends API_AdminProjectFilter, API_AdminProjectPagination {
  markUnassignable?: boolean; // Mark projects that currently cannot be assigned (due to high-security mode)
  showDeactivated?: boolean; // to filter projects with status "DEACTIVATED"
}

interface ProjectOverViewTableProps extends WithModal {
  prGroupId?: string;
  goToNewProject: (url?: string) => void;
  preservedSearchQuery: string;
}

type ProjectTypeEnums = API_AdminProject["type"] | API_AdminProject["typeSecretCloud"];

const API_PROJECTS_ADMIN: { [key in ProjectTypeEnums]: string } = {
  PROTECTED: "Protected",
  HIGH_SECURE: "High secure",
  HSM: "High secure (HSM)",
  NONE: "Default",
  MODERN: "Modern",
  CLASSIC: "Classic",
};

const sortByMap: {
  [key in "id" | "shortname" | "name" | "description" | "type"]: API_ADMINPROJECTPAGINATION_SORTBY;
} = {
  id: "ID",
  shortname: "SHORT_NAME",
  name: "NAME",
  description: "DESCRIPTION",
  type: "TYPE",
};
const sortOrderMap: { [key in SortOrder]: API_ADMINPROJECTPAGINATION_SORTORDER } = {
  ascend: "ASC",
  descend: "DESC",
};

export const ProjectOverViewTable = withModal(
  observer(({ showModal, ...props }: ProjectOverViewTableProps) => {
    const sortingSettings = userInfoStore.getSortingSettingsForAntdTable("admin_projects") || {
      order: "ascend",
      columnKey: "id",
    };

    const { t } = useTranslation([I18N_NS.ADMIN, I18N_NS.PROJECT]);
    const [searchString, setSearchString] = useState("");
    const [data, setData] = useState<API_AdminProject[]>([]);
    const [statusesCount, setStatusesCount] = useState<API_AdminProjectStatusesCount | null>(null);
    const [sorter, setSorter] = useState<SorterResult<API_AdminProject> | null>(sortingSettings);
    const [loading, setLoading] = useState(false);
    const [onlyActiveProjects, setOnlyActiveProjects] = useState(true);
    const [pagination, setPagination] = useState<PaginationProps>({
      showSizeChanger: true,
      total: 0,
      pageSize: 30,
      current: 1,
    });

    const updateData = useCallback(
      async (projectId: number, payload: API_PGAdminProjectChanges) => {
        try {
          await projectGroupList.updateData(projectId, payload);
          await handleTableChange(pagination, {}, sorter, searchString || props.preservedSearchQuery);
        } catch (error) {
          throw error;
        }
      },
      [pagination, sorter, searchString]
    );

    const getDropdownActionsForProject = useCallback(
      (record: API_AdminProject) => {
        const updateStatus = async (id?: number, status?: API_PGADMINPROJECTCHANGES_NEWPROJECTSTATUS) => {
          if (id) {
            await projectGroupList.updateData(id, { newProjectStatus: status });
          }
          await handleTableChange(pagination, {}, sorter, searchString || props.preservedSearchQuery);
        };

        const actions = [];
        if (userInfoStore.role === "PRGROUPADMIN") {
          const actionDisabledCreateProject = !projectGroupList.prAdminRightProject(props.prGroupId);
          const actionDisabledCreateUser = !projectGroupList.prAdminRightUser(props.prGroupId);
          actions.push(
            {
              label: "Activate project",
              onClick: (prId: number) =>
                modal.confirm({
                  title: t("Are you sure that you want to activate project {{name}}?", {
                    name: record.name,
                  }),
                  okText: t("Confirm"),
                  okType: "primary",
                  cancelText: t("Cancel"),
                  zIndex: 999999,
                  onOk() {
                    updateStatus(prId, "ENABLED");
                  },
                }),
              disabled: actionDisabledCreateProject || record.status === "ENABLED",
            },
            {
              label: "Deactivate project",
              onClick: (prId: number) =>
                modal.confirm({
                  title: t("Are you sure that you want to deactivate project {{name}}?", {
                    name: record.name,
                  }),
                  okText: t("Confirm"),
                  okType: "primary",
                  cancelText: t("Cancel"),
                  zIndex: 999999,
                  onOk() {
                    updateStatus(prId, "DEACTIVATED");
                  },
                }),
              disabled: actionDisabledCreateProject || record.status === "DEACTIVATED",
            },
            {
              label: "Archive project",
              onClick: (prId: number) =>
                modal.confirm({
                  title: t("Are you sure that you want to archive project {{name}}?", {
                    name: record.name,
                  }),
                  okText: t("Confirm"),
                  okType: "primary",
                  cancelText: t("Cancel"),
                  zIndex: 999999,
                  onOk() {
                    updateStatus(prId, "ARCHIVED");
                  },
                }),
              disabled: actionDisabledCreateProject || record.status === "ARCHIVED",
            },
            {
              label: "Edit name",
              onClick: (prId: number) => {
                showModal(EditProjectGroupNameModal, {
                  modalProps: {
                    footer: null,
                    width: "100%",
                    style: { maxWidth: 600 },
                    title: t("Edit project name"),
                    maskClosable: false,
                  },
                  componentProps: {
                    currentName: record.name,
                    projectId: prId,
                    updateData: updateData,
                  },
                });
              },
              disabled: actionDisabledCreateProject || record.status === "ARCHIVED",
            },
            {
              label: "Edit project description",
              onClick: (prId: number) => {
                showModal(EditProjectDescriptionModal, {
                  modalProps: {
                    footer: null,
                    width: "100%",
                    style: { maxWidth: 600 },
                    title: t("Edit project description"),
                    maskClosable: false,
                    destroyOnHidden: true,
                  },
                  componentProps: {
                    currentDescription: record.description,
                    projectId: prId,
                    editProjectDescription: updateData,
                  },
                });
              },
              disabled: actionDisabledCreateProject,
            },
            {
              label: "Information",
              onClick: (prId: number) => {
                showModal(ProjectInformationModal, {
                  modalProps: {
                    footer: null,
                    width: "100%",
                    style: { maxWidth: 1300 },
                    title: `${record.name}: ${t("Information")}`,
                    maskClosable: false,
                    destroyOnHidden: true,
                  },
                  componentProps: {
                    projectId: prId,
                  },
                });
              },
            },
            {
              label: t("Manage members"),
              disabled: actionDisabledCreateUser,
              onClick: () => {
                showModal(ProjectMembersModal, {
                  componentProps: {
                    projectId: record.id,
                    prGroupId: props.prGroupId,
                  },
                  modalProps: {
                    title: t("Members"),
                    footer: null,
                    style: { maxWidth: `calc(100vw - 40px)` },
                    width: "100%",
                    className: "full-height-modal",
                    maskClosable: false,
                    destroyOnHidden: true,
                  },
                });
              },
            }
          );
        } else {
          actions.push(
            {
              label: "Access project",
              // tslint:disable-next-line:max-line-length
              onClick: (prId: number) =>
                window.open(`${window.location.origin}/${window.GLOBAL.API_BASE}/project/${prId}/overview`),
              disabled: record.unassignable || record.typeSecretCloud !== "NONE",
            },
            {
              label: "Clone project",
              onClick: (prId: number) =>
                window.open(`${window.location.origin}/${window.GLOBAL.API_BASE}/admin/projects/${prId}/clone`),
              disabled: false,
            },
            {
              label: "Filename rules",
              onClick: (prId: number) =>
                window.open(
                  `${window.location.origin}/${window.GLOBAL.API_BASE}/admin/projects/${prId}/filename-rules`
                ),
              disabled: !record.functions?.FILENAMECHECK,
            },
            {
              label: "Metadata",
              onClick: (prId: number) =>
                window.open(`${window.location.origin}/${window.GLOBAL.API_BASE}/admin/projects/${prId}/metadata`),
              disabled: !record.functions?.METADATA,
            },
            {
              label: "Repro services",
              onClick: (prId: number) =>
                window.open(`${window.location.origin}/${window.GLOBAL.API_BASE}/admin/projects/${prId}/repro-service`),
              disabled: !record?.moduleOptions?.plotAndPrintOptions?.showingAlternativePlotServices,
            },
            {
              label: "Views",
              disabled: !record.modules.VIEW,
              onClick: (prId: number) =>
                window.open(`${window.location.origin}/${window.GLOBAL.API_BASE}/admin/projects/${prId}/views`),
            },
            {
              label: "Manage workflow",
              disabled: !record.modules.WORKFLOW,
              onClick: (prId: number) =>
                window.open(`${window.location.origin}/${window.GLOBAL.API_BASE}/admin/projects/${prId}/workflows`),
            },
            {
              label: "Directory mapping",
              disabled: !record.functions.AUTOMATICDIRMAPPING,
              onClick: (prId: number) =>
                window.open(`${window.location.origin}/${window.GLOBAL.API_BASE}/admin/projects/${prId}/auto-mapping`),
            },
            {
              label: "Change file owner",
              disabled: false,
              onClick: (prId: number) =>
                showModal(ChangeFileOwner, {
                  modalProps: {
                    footer: null,
                    width: 800,
                    title: t("Change file owner"),
                    maskClosable: false,
                    destroyOnHidden: true,
                  },
                  componentProps: {
                    projectId: prId,
                  },
                }),
            }
          );
        }
        return actions;
      },
      [showModal, updateData, pagination, sorter, searchString, props.prGroupId, t]
    );

    const getParams = useCallback(
      (
        paginationParam: PaginationProps,
        sorterParam: SorterResult<API_AdminProject>,
        overrideQuery?: string,
        overrideOnlyActiveProjects?: boolean
      ) => {
        const queryToUse = overrideQuery !== undefined ? overrideQuery : searchString;
        const onlyActiveProjectsToUse =
          overrideOnlyActiveProjects !== undefined ? overrideOnlyActiveProjects : onlyActiveProjects;
        const params: ProjectQueryParams = {
          limit: paginationParam.pageSize,
          offset: paginationParam.current > 1 ? paginationParam.pageSize * (paginationParam.current - 1) : 0,
          // by default set sorted by userId and ascending.
          sortBy: sorterParam ? sortByMap[(sorterParam.field || sorterParam.columnKey) as string] : "ID",
          sortOrder: sorterParam ? sortOrderMap[sorterParam.order] : "ASC",
          query: queryToUse.replace(/[&]/g, "%26"),
          markUnassignable: true,
          showDeactivated: !onlyActiveProjectsToUse,
        };
        if (props.prGroupId) {
          params.projectGroupId = +props.prGroupId;
        }
        return params;
      },
      [searchString, onlyActiveProjects, props.prGroupId]
    );

    const handleTableChange = useCallback(
      async (
        paginationParam: PaginationProps,
        filters: {},
        sorterParam: SorterResult<API_AdminProject>,
        overrideQuery?: string,
        overrideOnlyActiveProjects?: boolean
      ) => {
        try {
          setLoading(true);
          const params = getParams(paginationParam, sorterParam, overrideQuery, overrideOnlyActiveProjects);
          const response: API_MultiAdminProjectResponse = await API.getData(
            API_URL.ADMIN_PROJECTS.getAllProjects(),
            params
          );
          setPagination({ ...paginationParam, total: response.total });
          setSorter(sorterParam);
          userInfoStore.storeSortingSettings("admin_projects", {
            direction: sorterParam.order === "ascend" ? "asc" : "desc",
            sortBy: sorterParam.columnKey as string,
          });
          setData(response.items || []);
          setStatusesCount(response.statusesCount);
        } catch (error) {
          throw error;
        } finally {
          setLoading(false);
        }
      },
      [getParams]
    );

    const handleSearch = useCallback(
      async (searchValue: string) => {
        // Reset current page to 1 when trying to reset or change search query
        const updatedPagination = { ...pagination };
        if (!searchValue || searchValue !== searchString) {
          updatedPagination.current = 1;
        }
        setPagination(updatedPagination);
        setSearchString(searchValue);
        await handleTableChange(updatedPagination, {}, sorter, searchValue);
      },
      [pagination, searchString, setSearchString, setPagination, sorter, handleTableChange]
    );

    const renderDropdownActionButtons = useCallback(
      (text, record) => {
        const dropdownActions = getDropdownActionsForProject(record);

        const onClick = ({ key }) => {
          dropdownActions[key].onClick(record.id);
        };

        return (
          <Dropdown
            menu={{
              items: dropdownActions.map((item, idx) => ({
                key: idx,
                disabled: item.disabled,
                label: t(item.label),
              })),
              onClick: onClick,
            }}
            placement="bottomRight"
          >
            <span style={{ display: "inline-block", width: 60, textAlign: "center" }}>
              <FontAwesomeIcon className="email-action-icon" icon={["far", "ellipsis-v"]} fixedWidth={true} />
            </span>
          </Dropdown>
        );
      },
      [getDropdownActionsForProject]
    );

    const linkCell = useCallback(
      (text: string, record: API_AdminProject) =>
        userInfoStore.role === "PRGROUPADMIN" ? (
          text
        ) : (
          <Link to={{ pathname: `/admin/projects/edit/${record.id}` }} state={{ searchQuery: searchString }}>
            {text}
          </Link>
        ),
      [searchString]
    );

    const convertEnumToText = useCallback((text: string) => t(API_PROJECTS_ADMIN[text]) || text, [t]);

    const convertStatusEnumToBadge = useCallback(
      (text: string, record: API_AdminProject) => {
        switch (record.status) {
          case "ENABLED":
            return <Badge status="success" text={t("Active")} />;
          case "ARCHIVED":
            return <Badge status="warning" text={t("Archived")} />;
          case "DEACTIVATED":
            return <Badge status="error" text={t("Deactivated")} />;
          default:
            return null;
        }
      },
      [t]
    );

    const projectExtras = useCallback((text, record): React.ReactNode => {
      return text ? (
        <Tooltip title={record.extrasNote}>
          <FontAwesomeIcon
            icon={["fas", "comment-exclamation"]}
            style={{ color: record.status === "ENABLED" && "#3d3d3d" }}
          />
        </Tooltip>
      ) : (
        ""
      );
    }, []);

    const handleSwitcher = useCallback(
      async (checked: boolean) => {
        setOnlyActiveProjects(checked);
        await waitFor(200);
        handleTableChange(pagination, {}, sorter, searchString || props.preservedSearchQuery, checked);
      },
      [pagination, sorter, searchString, props.preservedSearchQuery, onlyActiveProjects]
    );

    const projectType = useCallback(
      (text): React.ReactNode => {
        const type = text === "NONE" ? "-" : text;
        return convertEnumToText(type);
      },
      [convertEnumToText]
    );

    const handleTableChangeWrapper = useCallback(
      (paginationParam: PaginationProps, filters: {}, sorterParam: SorterResult<API_AdminProject>) => {
        handleTableChange(paginationParam, filters, sorterParam, searchString || props.preservedSearchQuery);
      },
      [searchString, props.preservedSearchQuery]
    );

    const columns = useMemo(
      () => [
        {
          key: "id",
          title: "Project id",
          dataIndex: "id",
          defaultSortOrder: "ascend" as const,
          sorter: true,
          width: 110,
          sortOrder: sorter.columnKey === "id" ? sorter.order : null,
        },
        {
          key: "name",
          title: "Name",
          dataIndex: "name",
          render: linkCell,
          sorter: true,
          ellipsis: true,
          width: 110,
          sortOrder: sorter.columnKey === "name" ? sorter.order : null,
        },
        {
          key: "type",
          title: t("Type"),
          dataIndex: "type",
          render: projectType,
          sorter: true,
          width: 100,
          sortOrder: sorter.columnKey === "type" ? sorter.order : null,
        },
        {
          key: "extras",
          title: t("Extras"),
          dataIndex: "extras",
          render: projectExtras,
          sorter: true,
          width: 100,
          sortOrder: sorter.columnKey === "extras" ? sorter.order : null,
        },
        {
          key: "status",
          title: t("Status"),
          dataIndex: "status",
          width: 180,
          render: convertStatusEnumToBadge,
          sortOrder: sorter.columnKey === "status" ? sorter.order : null,
        },
        {
          key: "typeSecretCloud",
          title: t("Security level"),
          dataIndex: "typeSecretCloud",
          width: 150,
          render: convertEnumToText,
          sortOrder: sorter.columnKey === "typeSecretCloud" ? sorter.order : null,
        },
        {
          title: t("Actions"),
          width: 74,
          render: renderDropdownActionButtons,
        },
      ],
      [
        sorter,
        linkCell,
        projectType,
        projectExtras,
        convertStatusEnumToBadge,
        convertEnumToText,
        renderDropdownActionButtons,
      ]
    );

    useEffect(() => {
      (async () => {
        try {
          if (props.prGroupId && !projectGroupList.data.length) {
            await projectGroupList.fetch();
          }
          if (props.preservedSearchQuery) {
            handleSearch(props.preservedSearchQuery);
          } else {
            handleTableChange(pagination, {}, sorter);
          }
        } catch (err) {
          throw err;
        }
      })();
    }, []);

    const canCreateNewProject =
      userInfoStore.role !== "PRGROUPADMIN" || projectGroupList.prAdminRightProject(props.prGroupId!);

    return (
      <>
        <Row justify="space-between" align="middle">
          <h1>
            {t("Project Overview")}
            {props.prGroupId && (
              <span>
                :<strong> {projectGroupList.getName(props.prGroupId)}</strong>
              </span>
            )}
          </h1>
          {userInfoStore.isAdmin && (
            <div>
              <Switch className="mr-5" onChange={handleSwitcher} defaultChecked={onlyActiveProjects} />
              {t("Show only active or archived projects")}
            </div>
          )}
        </Row>
        {statusesCount ? (
          <Row style={{ marginBottom: 10 }}>
            {t(
              // eslint-disable-next-line max-len
              "Currently you have {{active}} active, {{archived}} archived and {{deactivated}} deactivated projects.",
              {
                active: statusesCount?.totalActive,
                archived: statusesCount?.totalArchived,
                deactivated: statusesCount?.totalDeactivated,
              }
            )}
          </Row>
        ) : null}

        <DataFilterTable
          name="ProjectOverViewTable"
          rowKey="id"
          defaultSearchValue={props.preservedSearchQuery}
          searchOnChange={false}
          dataSource={data}
          pagination={pagination}
          loading={loading}
          rowClassName={(record) =>
            classNames("", { disabledCell: record.status === "DEACTIVATED" }, { greyCell: record.status !== "ENABLED" })
          }
          onChange={handleTableChangeWrapper}
          onSearch={handleSearch}
          placeholder={t("Search in id, names, descr., extras, customer")}
          actionButtons={
            canCreateNewProject
              ? [
                  {
                    iconName: "plus",
                    onClick: props.goToNewProject,
                    label: t("New project"),
                  },
                ]
              : []
          }
          columns={columns}
        ></DataFilterTable>
      </>
    );
  })
);
